import { useMutation } from '@tanstack/react-query';
import axiosRaw, { AxiosResponse } from 'axios';
import { decodeJwt } from 'jose';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { setAccessToken } from '../../config/axios';
import { useAuth, type User } from '../../hooks/useAuth';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	Button,
	ErrorMessage,
	FormContainer,
	FormGroup,
	Input,
	Label,
} from '../Shared/FormStyles';

import * as S from './Login.styles';

interface LoginFormData {
	username: string;
	password: string;
}

const Login = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, setUser } = useAuth();

	// Get redirect path from URL query param, then from location state, finally default to '/'
	const searchParams = new URLSearchParams(location.search);
	const redirectUrl = searchParams.get('redirect');
	const from = redirectUrl || location.state?.from?.pathname || '/';

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormData>();

	const loginMutation = useMutation({
		// Plain axios, NOT the auth axiosInstance: the instance's 401-interceptor
		// treats failed credentials as an expired session and hard-reloads to
		// /login (swallowing the 401 message — the 'unrelated error' bug).
		mutationFn: (data: LoginFormData) => axiosRaw.post('/api/login', data),
		onSuccess: (
			response: AxiosResponse<{
				accessToken: string;
				username: string;
				roles: string[];
				unitat: string;
			}>
		) => {
			const { accessToken } = response.data;
			setAccessToken(accessToken);
			const decoded = decodeJwt<User>(accessToken);
			setUser(decoded);
			navigate(from, { replace: true });
		},
	});

	const onSubmit = async (data: LoginFormData) => {
		loginMutation.mutate(data);
	};

	const loading = loginMutation.isPending;
	const error = loginMutation.error;

	// Already-authenticated visitors never get a login form — they get sent
	// straight back to inici (the "Ja estàs dins un compte!" bare page had no
	// way out and no reason to exist as a destination).
	useEffect(() => {
		if (user && !loading) {
			navigate('/inici', { replace: true });
		}
	}, [user, loading, navigate]);

	return user ? (
		<div>
			<div>Ja estàs dins un compte! ({user.username})</div>
		</div>
	) : (
		// NEVER replace the form with the error OR with a loading swap — both
		// would unmount the <form> mid-submission. Password managers track
		// submissions by form identity: an unmounted-then-remounted form makes
		// their save-prompt detection and autofill flaky. The form ALWAYS
		// renders; loading shows as the disabled/Entrant… button instead.
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			{error && (
				<ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
			)}
			<FormGroup>
				<Label htmlFor="username">Usuari</Label>
				<Input
					type="text"
					id="username"
					autoComplete="username"
					{...register('username', {
						required: "El nom d'usuari és obligatori!",
					})}
					placeholder="Nom d'usuari"
				/>
				{errors.username && (
					<ErrorMessage>
						{errors.username.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="password">Contrasenya</Label>
				<Input
					type="password"
					id="password"
					autoComplete="current-password"
					{...register('password', {
						required: 'Falta la contrasenya!',
					})}
					placeholder="Contrasenya"
				/>
				{errors.password && (
					<ErrorMessage>
						{errors.password.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			<S.ButtonWrapper>
				<Button type="submit" disabled={loginMutation.isPending}>
					{loginMutation.isPending ? 'Entrant...' : 'Entrar'}
				</Button>
			</S.ButtonWrapper>
			
			<S.CursiveMessage>Demana accés als caps!</S.CursiveMessage>
		</FormContainer>
	);
};

export default Login;
